/**
 * Kitchen groups for the Ingredients list, in the order a buyer walks an
 * order guide: proteins first, then dairy, produce, bakery, dry goods, and
 * the pantry.
 *
 * Built on the ingredient's USDA CNDB category, with name rules first:
 *
 *   1. Condiments by name. USDA files ketchup under Vegetables, ranch and
 *      mayonnaise under Fats and Oils, soy sauce under Legumes; a kitchen
 *      buys all of them as condiments.
 *   2. Milks by name. Soy and oat milk sit under Legumes or nowhere; they are
 *      bought with the milk.
 *   3. Tortillas and juices, which USDA splits across two categories each.
 *
 * Rows with no USDA category (a third of the catalog) fall back to keywords
 * in the name, then to "Everything else". Pure, unit-tested against the real
 * catalog names.
 */

export const INGREDIENT_GROUP_ORDER = [
  'Beef',
  'Pork',
  'Poultry',
  'Seafood',
  'Lamb & veal',
  'Deli meats & sausage',
  'Prepared foods',
  'Dairy & eggs',
  'Vegetables',
  'Fruit & juice',
  'Bakery',
  'Grains, pasta & cereal',
  'Beans, nuts & seeds',
  'Sauces & condiments',
  'Spices & herbs',
  'Oils & shortening',
  'Sweets & syrups',
  'Snacks',
  'Beverages',
  'Supplies',
  'Everything else',
] as const

export type IngredientGroupLabel = (typeof INGREDIENT_GROUP_ORDER)[number]

/** USDA CNDB category (`usda_cndb_categories.name`) → kitchen group. */
const USDA_CATEGORY_GROUPS: Readonly<Record<string, IngredientGroupLabel>> = {
  'Beef Products': 'Beef',
  'Pork Products': 'Pork',
  'Poultry Products': 'Poultry',
  'Finfish and Shellfish Products': 'Seafood',
  'Lamb, Veal, and Game Products': 'Lamb & veal',
  'Sausages and Luncheon Meats': 'Deli meats & sausage',
  'Meals, Entrees, and Side Dishes': 'Prepared foods',
  'Fast Foods': 'Prepared foods',
  'Restaurant Foods': 'Prepared foods',
  'Dairy and Egg Products': 'Dairy & eggs',
  'Vegetables and Vegetable Products': 'Vegetables',
  'Fruits and Fruit Juices': 'Fruit & juice',
  'Baked Products': 'Bakery',
  'Cereal Grains and Pasta': 'Grains, pasta & cereal',
  'Breakfast Cereals': 'Grains, pasta & cereal',
  'Legumes and Legume Products': 'Beans, nuts & seeds',
  'Nut and Seed Products': 'Beans, nuts & seeds',
  'Soups, Sauces, and Gravies': 'Sauces & condiments',
  'Spices and Herbs': 'Spices & herbs',
  'Fats and Oils': 'Oils & shortening',
  Sweets: 'Sweets & syrups',
  Snacks: 'Snacks',
  Beverages: 'Beverages',
}

/**
 * Name rules checked BEFORE the USDA category. "Mustard, Ground" is a spice,
 * so a dry mustard stays with the spices.
 */
const NAME_RULES: ReadonlyArray<{ match: (name: string) => boolean; group: IngredientGroupLabel }> = [
  {
    match: (name) =>
      /\b(ketchup|mayo|mayonnaise|ranch|dressing|relish|soy sauce|tamari|salsa|taco sauce|hot sauce|sriracha|bbq sauce|barbecue sauce|vinegar|liquid smoke)\b/i.test(
        name,
      ) ||
      (/\bmustard\b/i.test(name) && !/\b(ground|powder|dry|dried|seed|seeds)\b/i.test(name)),
    group: 'Sauces & condiments',
  },
  // Any milk, dairy or not, is bought with the milk.
  { match: (name) => /^milk\b|\bmilk,|\b(soy|oat|almond|rice|coconut) milk\b/i.test(name), group: 'Dairy & eggs' },
  // USDA files one tortilla under grains and the rest nowhere.
  { match: (name) => /\btortillas?\b/i.test(name), group: 'Bakery' },
  // USDA splits juice between Beverages and Fruits.
  { match: (name) => /\bjuice\b/i.test(name), group: 'Fruit & juice' },
]

/** Name keywords for rows with no USDA category, checked in this order. */
const KEYWORD_GROUPS: ReadonlyArray<{ match: RegExp; group: IngredientGroupLabel }> = [
  {
    match: /\b(pizza|nugget|nuggets|tender|tenders|orange chicken|teriyaki chicken|premade)\b/i,
    group: 'Prepared foods',
  },
  { match: /\b(pepperoni|salami|sausage|frankfurter|hot dog(?! bun)|bacon|deli)\b/i, group: 'Deli meats & sausage' },
  { match: /\b(hamburger patty|beef)\b/i, group: 'Beef' },
  { match: /\b(chicken|turkey)\b/i, group: 'Poultry' },
  { match: /\b(egg|eggs|cheese|yogurt|(?<!peanut |nut )butter)\b/i, group: 'Dairy & eggs' },
  {
    match:
      /\b(bread|bun|bagel|muffin|roll|flatbread|breadstick|waffle|pancake|pancakes|french toast|biscuit|breakfast round|cookie|croissant)\b/i,
    group: 'Bakery',
  },
  {
    match: /\b(cracker|crackers|pretzel|pretzels|cheez-it|goldfish|(?<!chocolate )chips|bar|bars)\b/i,
    group: 'Snacks',
  },
  { match: /\b(cereal|oats|oatmeal|cheerios|kix|granola|rice|pasta|quinoa)\b/i, group: 'Grains, pasta & cereal' },
  { match: /\b(sunbutter|peanut butter|nut butter|beans|chickpeas|nuts|seeds)\b/i, group: 'Beans, nuts & seeds' },
  { match: /\b(jam|jelly|syrup|spread|honey|chocolate|sugar)\b/i, group: 'Sweets & syrups' },
  {
    match:
      /\b(applesauce|apple|apples|banana|berries|cranberries|raisins|fruit|orange|grapes|melon|pear|pears|peach)\b/i,
    group: 'Fruit & juice',
  },
  {
    match:
      /\b(tomato|tomatoes|lettuce|carrot|carrots|onion|onions|potato|potatoes|pepper|peppers|corn|broccoli|spinach|cucumber)\b/i,
    group: 'Vegetables',
  },
  { match: /\b(seasoning|spice|herb|herbs|salt)\b/i, group: 'Spices & herbs' },
  { match: /\b(water|lemonade|drink)\b/i, group: 'Beverages' },
]

export interface IngredientGroupInput {
  name: string
  /** FOOD / SUPPLY / UNCATEGORIZED. */
  category: string
  usdaCategory: string | null
}

export function ingredientGroupFor(row: IngredientGroupInput): IngredientGroupLabel {
  const name = row.name
  for (const rule of NAME_RULES) {
    if (rule.match(name)) {
      return rule.group
    }
  }
  if (row.usdaCategory) {
    const group = USDA_CATEGORY_GROUPS[row.usdaCategory]
    if (group) {
      return group
    }
  }
  if (row.category === 'SUPPLY') {
    return 'Supplies'
  }
  for (const rule of KEYWORD_GROUPS) {
    if (rule.match.test(name)) {
      return rule.group
    }
  }
  return 'Everything else'
}

export interface IngredientGroup<T> {
  label: IngredientGroupLabel
  rows: T[]
}

/** Rows grouped in buying order; inside a group, by catalog name. Empty groups are dropped. */
export function groupIngredientsForBuying<T>(
  rows: readonly T[],
  describe: (row: T) => IngredientGroupInput,
): Array<IngredientGroup<T>> {
  const byGroup = new Map<IngredientGroupLabel, T[]>()
  for (const row of rows) {
    const group = ingredientGroupFor(describe(row))
    const bucket = byGroup.get(group)
    if (bucket) {
      bucket.push(row)
    } else {
      byGroup.set(group, [row])
    }
  }
  return INGREDIENT_GROUP_ORDER.filter((label) => byGroup.has(label)).map((label) => ({
    label,
    rows: [...byGroup.get(label)!].sort((a, b) => describe(a).name.localeCompare(describe(b).name)),
  }))
}
